import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera as CameraIcon, Trash2, X } from 'lucide-react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Button } from '@shared/ui/primitives/Button';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { Stepper } from '@shared/ui/lists/Stepper';
import {
  ActionButton,
  FormActions,
  FormSection,
  FormStack,
  SelectField,
  TextInputField,
} from '@shared/ui/forms/FormFields';
import { pageCopy } from '@shared/lib/pageCopy';
import { platform } from '@shared/lib/platform';
import { toast } from '@shared/ui/feedback/Toast';
import { focusNextOnEnter, applyEnterKeyHint } from '@shared/lib/formNav';
import { fetchProfile } from '@features/profile/api';
import { fetchMyProperties, type MemberProperty } from '@features/properties/api';
import {
  createVehicle,
  deleteVehicle,
  fetchActiveVehicleTypes,
  fetchMemberVehicles,
  updateVehicle,
  uploadVehiclePhoto,
} from './api';

type VehicleStep = 'details' | 'profile' | 'review';

const VEHICLE_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const VEHICLE_PHOTO_TARGET_BYTES = 4.5 * 1024 * 1024;
const VEHICLE_PHOTO_MAX_DIMENSION = 1600;

export function VehicleForm(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { vehicleId } = useParams<{ vehicleId?: string }>();
  const isEdit = Boolean(vehicleId);
  const formCopy = pageCopy().vehicles.form;
  const copy = isEdit ? formCopy.edit : formCopy.new;

  const profileQuery = useQuery({ queryKey: ['profile'], queryFn: fetchProfile });
  const memberId = profileQuery.data?.member_id ?? profileQuery.data?.memberId;

  const vehicleTypesQuery = useQuery({
    queryKey: ['vehicle-types-active'],
    queryFn: fetchActiveVehicleTypes,
  });
  const propertiesQuery = useQuery({
    queryKey: ['my-properties'],
    queryFn: fetchMyProperties,
    enabled: !!memberId,
  });
  const vehiclesQuery = useQuery({
    queryKey: ['vehicles', memberId],
    queryFn: () => fetchMemberVehicles(memberId!),
    enabled: !!memberId && isEdit,
  });
  const editingVehicle = isEdit
    ? vehiclesQuery.data?.find((vehicle) => vehicle.vehicle_id === vehicleId)
    : undefined;

  const [vehicleType, setVehicleType] = useState('');
  const [name, setName] = useState('');
  const [registration, setRegistration] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [colour, setColour] = useState('');
  const [propertyLocationId, setPropertyLocationId] = useState('');
  const [photo, setPhoto] = useState<{ blob: Blob; preview: string } | null>(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [step, setStep] = useState<VehicleStep>('details');

  useEffect(() => {
    if (!isEdit || hydrated || !editingVehicle) return;
    setVehicleType(editingVehicle.vehicle_type ?? '');
    setName(editingVehicle.name ?? '');
    setRegistration(editingVehicle.registration ?? '');
    setMake(editingVehicle.make ?? '');
    setModel(editingVehicle.model ?? '');
    setColour(editingVehicle.colour ?? '');
    setPropertyLocationId(editingVehicle.property_location_id ?? '');
    setExistingPhotoUrl(editingVehicle.photo_url ?? null);
    setStep('details');
    setHydrated(true);
  }, [editingVehicle, hydrated, isEdit]);

  useEffect(() => {
    if (isEdit || propertyLocationId) return;
    const onlyProperty = propertiesQuery.data?.length === 1 ? propertiesQuery.data[0] : null;
    if (onlyProperty?.property_id) {
      setPropertyLocationId(onlyProperty.property_id);
    }
  }, [isEdit, propertyLocationId, propertiesQuery.data]);

  useEffect(() => {
    return () => {
      if (photo?.preview) URL.revokeObjectURL(photo.preview);
    };
  }, [photo]);

  const steps = ['Details', 'Photo', 'Review'];
  const currentStep = step === 'details' ? 0 : step === 'profile' ? 1 : 2;
  const previewUrl = photo?.preview ?? existingPhotoUrl;
  const selectedProperty =
    propertiesQuery.data?.find((property) => property.property_id === propertyLocationId) ?? null;

  const save = useMutation({
    mutationFn: async () => {
      if (!memberId) throw new Error('No member profile found.');
      const payload = {
        vehicle_type: vehicleType,
        name: name.trim(),
        registration: normalizeVehicleRegistration(registration) || undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
        colour: colour.trim() || undefined,
        member_id: memberId,
        property_location_id: propertyLocationId || null,
      };
      const saved =
        isEdit && vehicleId
          ? await updateVehicle(vehicleId, payload)
          : await createVehicle(payload);
      if (photo) {
        try {
          await uploadVehiclePhoto(saved.vehicle_id, photo.blob);
        } catch (error) {
          if (!isEdit) {
            try {
              await deleteVehicle(saved.vehicle_id);
            } catch {
              // Best-effort rollback only; surface the upload error below.
            }
          }
          throw error;
        }
      }
      return saved;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vehicles', memberId] });
      toast.success(isEdit ? 'Vehicle updated' : 'Vehicle added');
      navigate('/app/vehicles', { replace: true });
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : 'Could not save vehicle.';
      toast.danger('Could not save vehicle', message);
    },
  });

  const removeVehicle = useMutation({
    mutationFn: async () => {
      if (!vehicleId) throw new Error('Vehicle could not be removed.');
      await deleteVehicle(vehicleId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vehicles', memberId] });
      toast.success('Vehicle removed');
      navigate('/app/vehicles', { replace: true });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Could not remove vehicle.';
      toast.danger('Could not remove vehicle', message);
    },
  });

  const pickPhoto = async () => {
    if (attaching) return;
    setAttaching(true);
    try {
      let blob: Blob;
      if (platform.isNative()) {
        const shot = await Camera.getPhoto({
          quality: 80,
          resultType: CameraResultType.Uri,
          source: CameraSource.Prompt,
        });
        if (!shot.webPath) return;
        blob = await fetch(shot.webPath).then((r) => r.blob());
      } else {
        const file = await new Promise<File | null>((resolve) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = () => resolve(input.files?.[0] ?? null);
          input.click();
        });
        if (!file) return;
        blob = file;
      }
      const preparedPhoto = await compressVehiclePhoto(blob);
      if (preparedPhoto.size > VEHICLE_PHOTO_MAX_BYTES) {
        throw new Error('This photo is still too large after compression. Please choose a smaller image.');
      }
      setPhoto((prev) => {
        if (prev) URL.revokeObjectURL(prev.preview);
        return { blob: preparedPhoto, preview: URL.createObjectURL(preparedPhoto) };
      });
    } catch (e) {
      toast.danger("Couldn't add photo", e instanceof Error ? e.message : undefined);
    } finally {
      setAttaching(false);
    }
  };

  const removePhoto = () => {
    setPhoto((prev) => {
      if (prev) URL.revokeObjectURL(prev.preview);
      return null;
    });
    setExistingPhotoUrl(null);
  };

  const header = (
    <HeroHeader
      backTo="/app/vehicles"
      backBehavior="parent"
      eyebrow={copy.eyebrow}
      title={copy.title}
      subtitle={copy.subtitle}
      titleClassName="whitespace-pre-line leading-tight"
    />
  );

  if (isEdit && (vehiclesQuery.isLoading || profileQuery.isLoading)) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="flex flex-col gap-4 px-5 pt-5">
          <Skeleton className="h-24" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      </div>
    );
  }

  if (!isEdit && profileQuery.isLoading) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="flex flex-col gap-4 px-5 pt-5">
          <Skeleton className="h-24" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      </div>
    );
  }

  if (profileQuery.isError || vehicleTypesQuery.isError) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <ErrorState
            title="Couldn't load vehicle settings"
            description="Please try again. The vehicle form needs your latest tenant settings before it can continue."
            onRetry={() => {
              profileQuery.refetch();
              vehicleTypesQuery.refetch();
            }}
          />
        </div>
      </div>
    );
  }

  if (!memberId) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <ErrorState
            title="No member profile found"
            description="Vehicles are linked to your member profile. Please contact estate management if this keeps happening."
          />
        </div>
      </div>
    );
  }

  if (isEdit && !vehiclesQuery.isLoading && !editingVehicle) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <ErrorState title="Vehicle not found" onRetry={() => navigate('/app/vehicles')} />
        </div>
      </div>
    );
  }

  const propertyFieldError = propertiesQuery.isError
    ? 'Could not load your approved properties right now.'
    : undefined;
  const propertyHelper = propertiesQuery.isLoading
    ? 'Loading your approved properties...'
    : !propertiesQuery.data?.length
      ? 'No approved properties found for your profile yet.'
      : 'Choose the property this vehicle belongs to.';

  const canSubmit = !!memberId && !!vehicleType && !!name.trim();
  const canAdvanceDetails =
    !!vehicleType && !!name.trim() && !propertiesQuery.isError;
  const canAdvancePhoto = true;

  const validateDetailsStep = (): boolean => {
    if (!vehicleType) {
      toast.danger('Check the form', 'Please select a vehicle type.');
      return false;
    }
    if (!name.trim()) {
      toast.danger('Check the form', 'Name is required.');
      return false;
    }
    if (registration.trim() && !isValidVehicleRegistration(registration)) {
      toast.danger('Check the form', 'Registration must include letters and numbers.');
      return false;
    }
    if (propertiesQuery.isError) {
      toast.danger('Could not continue', 'Your properties could not be loaded. Please try again.');
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (step === 'details') {
      if (!validateDetailsStep()) return;
      setStep('profile');
      return;
    }

    if (step === 'profile') {
      setStep('review');
    }
  };

  const goBack = () => {
    if (step === 'review') {
      setStep('profile');
      return;
    }
    if (step === 'profile') {
      setStep('details');
    }
  };

  return (
    <div className="min-h-full bg-paper">
      {header}

      <FormStack className="pt-5">
        <div onKeyDown={focusNextOnEnter} onFocus={applyEnterKeyHint} className="flex flex-col gap-5">
          <FormSection className="pb-5" contentClassName="gap-3">
            <div>
              <h2 className="text-label font-semibold text-ink">
                {step === 'details' ? 'Add the vehicle details' : step === 'profile' ? 'Add a photo' : 'Review vehicle details'}
              </h2>
              <p className="mt-1 text-caption text-ink-muted">
                {step === 'details'
                  ? 'Capture the main details for this vehicle in one place before continuing.'
                  : step === 'profile'
                    ? 'Add an optional photo to help the estate identify this vehicle faster.'
                    : 'Double-check everything before you save this vehicle.'}
              </p>
            </div>
            <Stepper
              total={steps.length}
              current={currentStep}
              labels={steps}
              onStepSelect={(index) => {
                if (index < 0 || index > currentStep) return;
                setStep(index === 0 ? 'details' : index === 1 ? 'profile' : 'review');
              }}
              isStepAccessible={(index) => index <= currentStep}
            />
          </FormSection>

          {step === 'details' ? (
            <>
              <FormSection
                title="Vehicle details"
                description="These details help the estate identify the right vehicle in your household."
              >
                <SelectField
                  label="Vehicle type"
                  value={vehicleType}
                  onChange={(e) => setVehicleType(e.target.value)}
                  disabled={vehicleTypesQuery.isLoading}
                  required
                >
                  <option value="">Choose vehicle type</option>
                  {(vehicleTypesQuery.data ?? []).map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </SelectField>

                <TextInputField
                  label="Vehicle name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Family SUV"
                  autoCapitalize="words"
                  required
                />

                <TextInputField
                  label="Registration"
                  value={registration}
                  onChange={(e) => setRegistration(e.target.value.toUpperCase())}
                  placeholder="e.g. CA 123 456"
                  helper="Include both letters and numbers if you add a registration."
                />

                <TextInputField
                  label="Make"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  placeholder="Optional"
                  optional
                />

                <TextInputField
                  label="Model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="Optional"
                  optional
                />

                <TextInputField
                  label="Colour"
                  value={colour}
                  onChange={(e) => setColour(e.target.value)}
                  placeholder="Optional"
                  optional
                />

                <SelectField
                  label="Property"
                  value={propertyLocationId}
                  onChange={(e) => setPropertyLocationId(e.target.value)}
                  disabled={propertiesQuery.isLoading || !propertiesQuery.data?.length}
                  optional
                  helper={propertyHelper}
                  error={propertyFieldError}
                >
                  <option value="">
                    {propertiesQuery.data?.length ? 'Choose property' : 'No properties available'}
                  </option>
                  {(propertiesQuery.data ?? []).map((property) => (
                    <option key={property.property_id} value={property.property_id}>
                      {formatPropertyLabel(property)}
                    </option>
                  ))}
                </SelectField>
              </FormSection>

              {isEdit ? (
                <FormSection
                  title="Remove vehicle"
                  description="Deleting a vehicle is permanent. This action is only available from the edit screen."
                >
                  <Button
                    type="button"
                    variant="danger"
                    width="full"
                    loading={removeVehicle.isPending}
                    onClick={() => {
                      const vehicleName = name.trim() || 'this vehicle';
                      if (!window.confirm(`Remove ${vehicleName}? This cannot be undone.`)) return;
                      removeVehicle.mutate();
                    }}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                    Remove vehicle
                  </Button>
                </FormSection>
              ) : null}
            </>
          ) : null}

          {step === 'profile' ? (
            <FormSection
              title="Photo"
              description="A photo helps security and management identify your vehicle faster."
            >
              {previewUrl ? (
                <div className="flex items-center gap-3 rounded-md border border-hairline-strong bg-elevated px-3.5 py-3">
                  <img src={previewUrl} alt="Vehicle" className="h-16 w-16 rounded-lg object-cover hairline" />
                  <div className="min-w-0 flex-1">
                    <p className="text-body-sm font-semibold text-ink">Photo selected</p>
                    <button
                      type="button"
                      onClick={() => void pickPhoto()}
                      className="mt-0.5 text-caption text-primary-700"
                    >
                      Change photo
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={removePhoto}
                    aria-label="Remove photo"
                    className="flex h-8 w-8 items-center justify-center rounded-pill bg-sunken text-ink-secondary"
                  >
                    <X className="h-4 w-4" strokeWidth={2} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => void pickPhoto()}
                  disabled={attaching}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-hairline-strong bg-elevated py-3.5 text-body-sm font-medium text-ink-secondary transition-colors disabled:opacity-60"
                >
                  <CameraIcon className="h-4.5 w-4.5" strokeWidth={1.5} />
                  {attaching ? 'Adding…' : 'Add a photo'}
                </button>
              )}
              <p className="text-caption text-ink-muted">A photo is optional, but helpful for estate records.</p>
            </FormSection>
          ) : null}

          {step === 'review' ? (
            <FormSection
              title="Review"
              description="Make sure the details below are correct before saving."
            >
              {previewUrl ? (
                <div className="flex items-center gap-3 rounded-md border border-hairline-strong bg-elevated px-3.5 py-3">
                  <img src={previewUrl} alt="Vehicle" className="h-16 w-16 rounded-lg object-cover hairline" />
                  <div>
                    <p className="text-body-sm font-semibold text-ink">Photo attached</p>
                    <p className="text-caption text-ink-muted">This photo will be saved with the vehicle profile.</p>
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-dashed border-hairline-strong bg-elevated px-3.5 py-3 text-caption text-ink-muted">
                  No photo added.
                </div>
              )}

              <ReviewRow
                label="Vehicle type"
                value={vehicleTypesQuery.data?.find((type) => type.value === vehicleType)?.label ?? '-'}
              />
              <ReviewRow label="Vehicle name" value={name.trim() || '-'} />
              <ReviewRow label="Registration" value={normalizeVehicleRegistration(registration) || '-'} />
              <ReviewRow label="Make" value={make.trim() || '-'} />
              <ReviewRow label="Model" value={model.trim() || '-'} />
              <ReviewRow label="Colour" value={colour.trim() || '-'} />
              <ReviewRow
                label="Property"
                value={selectedProperty ? formatPropertyLabel(selectedProperty) : '-'}
              />
            </FormSection>
          ) : null}

          <FormActions sticky>
            {step === 'details' ? (
              <ActionButton disabled={!canAdvanceDetails} onClick={goNext}>
                Next
              </ActionButton>
            ) : step === 'profile' ? (
              <div className="grid grid-cols-2 gap-3">
                <Button type="button" variant="secondary" width="full" onClick={goBack}>
                  Back
                </Button>
                <Button type="button" width="full" disabled={!canAdvancePhoto} onClick={goNext}>
                  Next
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Button type="button" variant="secondary" width="full" onClick={goBack}>
                  Back
                </Button>
                <ActionButton
                  disabled={!canSubmit}
                  loading={save.isPending}
                  onClick={() => {
                    if (!validateDetailsStep()) return;
                    save.mutate();
                  }}
                >
                  {isEdit ? 'Save changes' : 'Add vehicle'}
                </ActionButton>
              </div>
            )}
          </FormActions>
        </div>
      </FormStack>
    </div>
  );
}

function formatPropertyLabel(property: MemberProperty): string {
  return [property.unit_label, property.address].filter(Boolean).join(' - ');
}

function ReviewRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-hairline-strong bg-elevated px-3.5 py-3">
      <span className="text-caption font-semibold uppercase tracking-[0.12em] text-ink-muted">{label}</span>
      <span className="text-right text-body-sm text-ink">{value}</span>
    </div>
  );
}

function normalizeVehicleRegistration(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, ' ');
}

function isValidVehicleRegistration(value: string): boolean {
  const registration = normalizeVehicleRegistration(value);
  return /^(?=.*[A-Z])(?=.*\d)[A-Z0-9 -]+$/.test(registration);
}

async function compressVehiclePhoto(blob: Blob): Promise<Blob> {
  if (!blob.type.startsWith('image/')) return blob;

  const image = await loadImage(blob);
  let maxDimension = VEHICLE_PHOTO_MAX_DIMENSION;
  let quality = 0.82;
  let output = await renderCompressedImage(image, maxDimension, quality);

  if (
    blob.size <= VEHICLE_PHOTO_TARGET_BYTES &&
    Math.max(image.naturalWidth, image.naturalHeight) <= VEHICLE_PHOTO_MAX_DIMENSION
  ) {
    return blob;
  }

  while (output.size > VEHICLE_PHOTO_TARGET_BYTES && quality > 0.45) {
    quality -= 0.1;
    output = await renderCompressedImage(image, maxDimension, quality);
  }

  while (output.size > VEHICLE_PHOTO_TARGET_BYTES && maxDimension > 900) {
    maxDimension -= 200;
    quality = Math.min(quality, 0.72);
    output = await renderCompressedImage(image, maxDimension, quality);

    while (output.size > VEHICLE_PHOTO_TARGET_BYTES && quality > 0.45) {
      quality -= 0.08;
      output = await renderCompressedImage(image, maxDimension, quality);
    }
  }

  return output.size < blob.size ? output : blob;
}

async function loadImage(blob: Blob): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not read this image.'));
      img.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function renderCompressedImage(
  image: HTMLImageElement,
  maxDimension: number,
  quality: number,
): Promise<Blob> {
  const { width, height } = fitWithinBounds(image.naturalWidth, image.naturalHeight, maxDimension);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare this image for upload.');

  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });

  if (!blob) throw new Error('Could not compress this image.');
  return blob;
}

function fitWithinBounds(
  width: number,
  height: number,
  maxDimension: number,
): { width: number; height: number } {
  if (Math.max(width, height) <= maxDimension) {
    return { width, height };
  }

  const scale = maxDimension / Math.max(width, height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}
